import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🔗 Wiki - Seção Integração Bling
 * 
 * Documentação completa sobre a integração com o Bling ERP.
 * Explica como conectar, configurar regras e sincronizar dados.
 * Voltada para produtores (não técnica).
 */
const BlingWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:link-variant" />
          <span>Integração Bling</span>
        </div>
        <p className={styles.heroText}>
          Conecte sua conta Bling ERP e sincronize automaticamente vendas, produtos e estoque. 
          Automatize processos e ganhe tempo com a integração completa.
        </p>
      </section>

      {/* O que é a Integração Bling */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é a Integração Bling?</span>
          <p className={styles.sectionSubtitle}>
            Conexão automática com seu ERP
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              A <strong>Integração Bling</strong> permite que você conecte sua conta do Bling ERP 
              com a plataforma Pagmus. Quando configurada, a integração <strong>sincroniza 
              automaticamente</strong> vendas, produtos, estoque e outras informações entre os sistemas.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Isso significa que você não precisa <strong>cadastrar manualmente</strong> cada venda 
              no Bling. Tudo acontece automaticamente quando uma venda é confirmada na Pagmus!
            </p>
          </div>
        </div>
      </section>

      {/* Como Conectar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Conectar sua Conta Bling?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para configurar a integração
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-key" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Obtenha sua API Key do Bling
                </span>
                <p>
                  Acesse seu painel do Bling e gere uma <strong>API Key</strong> (chave de API). 
                  Você precisará dessa chave para conectar as contas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:link-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acesse a Página de Integração
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Operações → Integrações → Integração Bling ERP</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:form-select" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Configure a Conexão
                </span>
                <p>
                  Cole sua <strong>API Key do Bling</strong> no campo indicado e clique em 
                  <strong>"Conectar"</strong> ou <strong>"Salvar"</strong>. A conexão será testada automaticamente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Verifique a Conexão
                </span>
                <p>
                  Após conectar, verifique se a <strong>conta está ativa</strong> e funcionando. 
                  Você verá informações da sua conta Bling na tela.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Configurar Regras */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Configurar Regras de Sincronização</span>
          <p className={styles.sectionSubtitle}>
            Defina quando e como os dados são sincronizados
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Produtos</div>
                <span className={styles.cardTitle}>Vincular Produtos</span>
                <p>
                  Para cada produto da Pagmus, você pode <strong>vincular um SKU do Bling</strong>. 
                  Isso permite que as vendas sejam sincronizadas corretamente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Momento</div>
                <span className={styles.cardTitle}>Quando Sincronizar</span>
                <p>
                  Escolha <strong>quando</strong> a sincronização deve acontecer: 
                  imediatamente após a venda, em horários específicos, ou manualmente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:bank" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Conta</div>
                <span className={styles.cardTitle}>Conta de Destino</span>
                <p>
                  Selecione a <strong>conta do Bling</strong> onde as vendas devem ser registradas. 
                  Útil se você tem múltiplas contas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:settings" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Configurações</div>
                <span className={styles.cardTitle}>Opções Avançadas</span>
                <p>
                  Configure <strong>opções adicionais</strong>: sincronizar estoque, atualizar 
                  preços, criar clientes automaticamente, etc.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona a Sincronização?</span>
          <p className={styles.sectionSubtitle}>
            Fluxo automático de dados
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              Quando uma venda é confirmada na Pagmus:
            </p>
            <ol style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li>A plataforma <strong>detecta a venda confirmada</strong></li>
              <li>Verifica se há <strong>regras de sincronização</strong> configuradas para o produto</li>
              <li>Busca o <strong>SKU correspondente</strong> no Bling</li>
              <li>Cria automaticamente a <strong>venda no Bling</strong> com todos os dados</li>
              <li>Atualiza o <strong>estoque</strong> (se configurado)</li>
              <li>Registra o <strong>histórico</strong> da sincronização</li>
            </ol>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Tudo acontece <strong>automaticamente</strong> em segundos! Você não precisa fazer nada manualmente.
            </p>
          </div>
        </div>
      </section>

      {/* Histórico */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Histórico de Sincronização</span>
          <p className={styles.sectionSubtitle}>
            Acompanhe todas as sincronizações realizadas
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:history" className={styles.infoIcon} />
          <div>
            <p>
              Na página de integração, você encontra um <strong>histórico completo</strong> de todas 
              as sincronizações realizadas. Para cada sincronização, você pode ver:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Data e hora:</strong> Quando a sincronização aconteceu</li>
              <li><strong>Produto:</strong> Qual produto foi sincronizado</li>
              <li><strong>Status:</strong> Se foi bem-sucedida ou houve erro</li>
              <li><strong>Detalhes:</strong> Informações sobre a venda sincronizada</li>
              <li><strong>Erros:</strong> Se houver problemas, você verá a mensagem de erro</li>
            </ul>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Use o histórico para <strong>verificar se tudo está funcionando corretamente</strong> 
              e identificar possíveis problemas.
            </p>
          </div>
        </div>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Como usar a integração de forma eficiente
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Mantenha a API Key Segura
                </span>
                <p>
                  Sua <strong>API Key do Bling</strong> é sensível. Não compartilhe com ninguém 
                  e mantenha-a segura. Se suspeitar de uso indevido, gere uma nova chave.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:link-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Vincule Todos os Produtos
                </span>
                <p>
                  Para que a sincronização funcione, você precisa <strong>vincular cada produto 
                  da Pagmus a um SKU do Bling</strong>. Configure isso nas regras.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Monitore o Histórico
                </span>
                <p>
                  <strong>Verifique regularmente</strong> o histórico de sincronização para garantir 
                  que tudo está funcionando. Erros aparecem claramente no histórico.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:test-tube" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Teste Antes de Ativar
                </span>
                <p>
                  Após configurar, <strong>faça uma venda de teste</strong> e verifique se a 
                  sincronização funcionou corretamente antes de ativar em produção.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default BlingWiki;

